import { requireAnyOrgPermission, requireAnySystemPermission, requireOrgPermission, } from '@juvare/contact-management/backend/features/permissions/shared';
/**
 * Normalize and validate the notification sender based on the current user's permissions.
 *
 * - `system_*` senders require the user to have the **system** permission: `'manage-notifications'`.
 * - `account_*` senders require the user to have **any org** permission: `'view-and-message-members'`.
 *   When allowed, the sender is normalized to the **current user**: `'account_' + userState.id`
 *   (i.e., it ignores whatever account id was passed in).
 * - `org_*` senders require either:
 *   - Org-level permissions on that org: `'view-and-message-members'` **and** `'create-notifications'`, or
 *   - System permission: `'all-organizations-admin'`.
 * - Empty/falsey `ownerId` clears to `''` with reason `'empty'`.
 * - Any other (unknown) prefix is returned unchanged with reason `'unknown-prefix'`.
 *
 * @param {UserState} userState - The current user's state (must include `id`). Used by permission checks.
 * @param {string} ownerId - The requested sender id (`system_*`, `account_*`, `org_*`, or other).
 * @returns {NormalizeResult} The normalized sender and (optionally) a reason when cleared or unknown for better debugging.
 */
export function normalizeNotificationSender(userState, ownerId) {
    if (!ownerId)
        return '';
    // System sender: only allowed with manage-notifications
    if (ownerId.startsWith('system_')) {
        const allowed = requireAnySystemPermission(userState, 'manage-notifications') ||
            (requireAnyOrgPermission(userState, 'view-and-message-members', 'create-notifications') &&
                requireAnyOrgPermission(userState, 'notify-as-system'));
        return allowed ? ownerId : '';
    }
    // Account sender: requires view-and-message-members somewhere
    if (ownerId.startsWith('account_')) {
        const allowed = requireAnyOrgPermission(userState, 'view-and-message-members');
        return allowed ? 'account_' + userState.id : '';
    }
    // Organization sender: org-level permission OR system all-orgs-admin
    if (ownerId.startsWith('org_')) {
        const orgId = ownerId.slice('org_'.length);
        const allowed = requireOrgPermission(userState, orgId, 'view-and-message-members', 'create-notifications') ||
            requireAnySystemPermission(userState, 'all-organizations-admin');
        return allowed ? ownerId : '';
    }
    // Unknown prefix — keep as-is or clear depending on your policy
    return ownerId;
}
